/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

const DEFAULT_TIMEOUT_SECONDS = 600;
const EXPONENTIAL_BACKOFF_EXPONENT = 3;

export const fetchRetry = async (
  url: RequestInfo | URL,
  options: RequestInit = {},
  timeoutSeconds: number = DEFAULT_TIMEOUT_SECONDS,
  retries: number = 3,
  retryCount: number = 0,
  exponentialBackoffExponent: number = EXPONENTIAL_BACKOFF_EXPONENT
): Promise<any> => {
  try {
    const response: Response | void = await Promise.race<Response | void>([
      fetch(url, options),
      new Promise((_resolve, reject) =>
        setTimeout(
          () =>
            reject(
              new Error(`Request timed out after ${timeoutSeconds} seconds.`)
            ),
          timeoutSeconds * 1000
        )
      ),
    ]);
    if (!(response as Response).ok) {
      if ([429, 503].includes((response as Response).status) && retries > 0) {
        retryCount++;
        retries--;
        // wait with exponential backoff
        await sleep(retryCount * exponentialBackoffExponent);
        return fetchRetry(
          url,
          options,
          timeoutSeconds,
          retries,
          retryCount,
          exponentialBackoffExponent
        );
      } else {
        throw "Request failed";
      }
    }
    return response;
  } catch (error: any) {
    if (
      error.message === "Timeout" ||
      error.message === "Network request failed" ||
      options.method === "GET" ||
      options.method === "HEAD"
    ) {
      if (retries > 0) {
        retryCount++;
        retries--;
        // wait with exponential backoff
        await sleep(retryCount ** exponentialBackoffExponent);
        return fetchRetry(
          url,
          options,
          timeoutSeconds,
          retries,
          retryCount,
          exponentialBackoffExponent
        );
      }
    } else {
      throw error;
    }
  }
};

function sleep(seconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, seconds * 1000));
}
